import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Edit3, RotateCcw } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { formatINR } from '@/data/mockData';

export default function OCRReviewScreen() {
  const { goBack, navigate, screenParams, updateInvoiceStatus } = useApp();
  const { invoice, ocrData } = screenParams;

  const [fields, setFields] = useState(
    ocrData || {
      invoiceNo: invoice?.invoiceNo || '',
      invoiceDate: invoice?.date || '',
      buyerName: invoice?.buyer || '',
      totalAmount: invoice?.amount || 0,
      remarks: '',
    }
  );
  const [lineItems, setLineItems] = useState(ocrData?.lineItems || []);
  const [isEditing, setIsEditing] = useState(false);

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <div className="font-semibold">No invoice data</div>
        <button onClick={goBack} className="mt-4 text-[#2563EB] font-semibold">Go Back</button>
      </div>
    );
  }

  const handleConfirm = () => {
    updateInvoiceStatus(invoice.id, 'UNDER_REVIEW');
    navigate('UPLOAD_SUCCESS', { invoice: { ...invoice, status: 'UNDER_REVIEW' }, ocrData: fields });
  };

  const inputClass = `w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all ${isEditing ? 'bg-white' : 'bg-slate-50'}`;

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="bg-[#2563EB] px-4 pt-5 pb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="ocr-back-btn"
              onClick={goBack}
              className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center tap-scale"
            >
              <ArrowLeft size={18} color="white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-base">OCR Review</h1>
              <div className="text-white/70 text-xs">Confirm extracted details</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-green-400/20 rounded-xl px-3 py-1.5">
            <CheckCircle size={12} color="#4ADE80" />
            <span className="text-green-300 text-xs font-bold">Scanned</span>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 flex flex-col gap-4">
        {/* OCR accuracy badge */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-3 flex items-center gap-3">
          <CheckCircle size={20} color="#22C55E" className="flex-shrink-0" />
          <div>
            <div className="font-semibold text-green-700 text-sm">OCR Extraction Successful</div>
            <div className="text-green-600 text-xs">96% confidence · Please review and confirm</div>
          </div>
        </div>

        {/* Extracted fields */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs font-black text-slate-500 uppercase tracking-widest">Extracted Details</div>
            <button
              data-testid="toggle-edit-btn"
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center gap-1.5 text-xs font-bold text-[#2563EB] tap-scale"
            >
              <Edit3 size={12} />
              {isEditing ? 'Done' : 'Edit Fields'}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Invoice No</label>
              <input
                data-testid="ocr-invoice-no"
                readOnly={!isEditing}
                value={fields.invoiceNo}
                onChange={e => setFields({ ...fields, invoiceNo: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Invoice Date</label>
              <input
                data-testid="ocr-invoice-date"
                readOnly={!isEditing}
                value={fields.invoiceDate}
                onChange={e => setFields({ ...fields, invoiceDate: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Buyer Name</label>
            <input
              data-testid="ocr-buyer-name"
              readOnly={!isEditing}
              value={fields.buyerName}
              onChange={e => setFields({ ...fields, buyerName: e.target.value })}
              className={inputClass}
            />
          </div>

          <div className="mb-3">
            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Total Amount (₹)</label>
            <input
              data-testid="ocr-total-amount"
              readOnly={!isEditing}
              type="number"
              value={fields.totalAmount}
              onChange={e => setFields({ ...fields, totalAmount: parseFloat(e.target.value) || 0 })}
              className={inputClass}
            />
          </div>

          <div>
            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block mb-1">Remarks</label>
            <textarea
              data-testid="ocr-remarks"
              readOnly={!isEditing}
              value={fields.remarks}
              onChange={e => setFields({ ...fields, remarks: e.target.value })}
              placeholder="Optional remarks..."
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>
        </div>

        {/* Line items */}
        {lineItems.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Line Items</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-slate-400 font-bold uppercase pb-2">Product</th>
                    <th className="text-right text-slate-400 font-bold uppercase pb-2">Qty</th>
                    <th className="text-right text-slate-400 font-bold uppercase pb-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-50 last:border-0">
                      <td className="py-2">
                        <div className="font-semibold text-slate-700">{item.product}</div>
                        <div className="text-slate-400">{item.pack}</div>
                      </td>
                      <td className="py-2 text-right text-slate-600 font-medium">{item.qty}</td>
                      <td className="py-2 text-right rupee-value font-bold text-[#1E2744]">
                        ₹{formatINR(item.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50">
                    <td className="pt-2 font-bold text-slate-700" colSpan={2}>Total</td>
                    <td className="pt-2 text-right rupee-value font-black text-[#1E2744]">
                      ₹{formatINR(fields.totalAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            data-testid="ocr-confirm-btn"
            onClick={handleConfirm}
            className="w-full bg-[#1E2744] text-white rounded-xl py-4 font-bold text-sm tracking-wide tap-scale"
          >
            Confirm & Submit
          </button>
          <button
            data-testid="ocr-reupload-btn"
            onClick={goBack}
            className="w-full border-2 border-slate-200 text-slate-500 rounded-xl py-3.5 font-semibold text-sm tap-scale flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} />
            Re-upload Invoice
          </button>
        </div>
      </div>
    </div>
  );
}
